import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Lock, User, LogIn, Bike, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { useApiAuth, useLoginMutation, useLogoutMutation } from '@/modules/auth-api'

interface LocationState { from?: string }

export function AdminLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useApiAuth((s) => s.user)
  const isAuthed = useApiAuth((s) => s.isAuthed)
  const loginMut = useLoginMutation()
  const logoutMut = useLogoutMutation()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Nếu đã login + role ADMIN → redirect ngay
  useEffect(() => {
    if (isAuthed && user?.role === 'ADMIN') {
      const from = (location.state as LocationState)?.from ?? '/admin'
      navigate(from, { replace: true })
    }
  }, [isAuthed, user, location.state, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const res = await loginMut.mutateAsync({ identifier: username, password })
      if (res.user.role !== 'ADMIN') {
        // Không phải admin → logout + thông báo
        logoutMut.mutate()
        setError('Tài khoản không có quyền truy cập trang quản trị')
        return
      }
      const from = (location.state as LocationState)?.from ?? '/admin'
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err?.message ?? 'Đăng nhập thất bại')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-slate-50 to-brand-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[420px]">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted hover:text-brand-600 mb-5">
          <ArrowLeft className="w-4 h-4" /> Về trang chủ
        </Link>

        <div className="bg-surface border border-border rounded-2xl shadow-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-brand-600 flex items-center justify-center text-white">
              <Bike className="w-7 h-7" />
            </div>
            <div>
              <div className="text-xl font-bold text-brand-700">Xe Điện Việt</div>
              <div className="text-xs text-muted">Trang quản trị nội bộ</div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-text">Đăng nhập Admin</h1>
          <p className="text-sm text-muted mt-1">Nhập tài khoản để quản lý hệ thống</p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error && (
              <div className="rounded-md bg-danger/10 border border-danger/30 text-danger px-4 py-2.5 text-sm">
                {error}
              </div>
            )}

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Username / Email / SĐT</div>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username hoặc email"
                  autoComplete="username"
                  className="w-full h-11 rounded-md border border-border bg-surface pl-10 pr-3.5 text-sm outline-none focus:border-brand-500 transition"
                />
              </div>
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Mật khẩu</div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full h-11 rounded-md border border-border bg-surface pl-10 pr-10 text-sm outline-none focus:border-brand-500 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text"
                >
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={loginMut.isPending}
              className="w-full h-12 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition"
            >
              <LogIn className="w-4 h-4" />
              {loginMut.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
